import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  Close,
  CheckCircle
} from '@mui/icons-material';
import ScrumAILogo from './ScrumAILogo';
import { useAuth } from '../../context/AuthContext';

interface SignInDialogProps {
  onSuccess?: () => void;
}

export const SignInDialog: React.FC<SignInDialogProps> = ({ onSuccess }) => {
  const navigate = useNavigate();
  const {
    signInModalOpen,
    closeSignInModal,
    targetAction,
    signInWithGoogle,
    loginAsDemoUser,
    loading
  } = useAuth();

  const handleGoogleClick = async () => {
    const success = await signInWithGoogle();
    if (success && onSuccess) {
      onSuccess();
    }
  };

  const handleDemoClick = () => {
    loginAsDemoUser();
    if (onSuccess) {
      onSuccess();
    }
  };

  const actionTitle = targetAction === 'workspace'
    ? 'Sign in to Access ScrumAIApp Workspace'
    : targetAction === 'inviteNotetaker'
    ? 'Sign in to ScrumAIApp'
    : 'Sign in to ScrumAIApp';

  const actionDesc = targetAction === 'workspace'
    ? 'Connect your Google account to access the 3-column AI dashboard, view live Jira epics and stories, and track real-time Scrum health scores.'
    : 'Connect your Google account to access meeting intelligence, generate Jira tickets, and collaborate with your agile team.';

  return (
    <Dialog
      open={signInModalOpen}
      onClose={closeSignInModal}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: '24px',
            p: 1.5,
            bgcolor: 'var(--hp-surface-raised)',
            boxShadow: '12px 12px 32px var(--hp-shadow-dark), -12px -12px 32px var(--hp-shadow-light)',
            border: '1px solid var(--hp-border)'
          }
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <ScrumAILogo fontSize="1.25rem" iconSize={28} />
        <IconButton size="small" onClick={closeSignInModal} sx={{ color: 'var(--hp-text-secondary)' }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--hp-text)', lineHeight: 1.25 }}>
            {actionTitle}
          </Typography>
          <Typography variant="body2" color="var(--hp-text-secondary)" sx={{ mt: 0.8, lineHeight: 1.5 }}>
            {actionDesc}
          </Typography>
        </Box>

        {/* Features highlight */}
        <Box
          className="neumorphic-card-subtle"
          sx={{
            p: 2,
            bgcolor: 'var(--hp-surface)',
            borderRadius: '16px',
            border: '1px solid var(--hp-border)',
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}
        >
          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: 'var(--hp-text)', fontWeight: 500 }}>
            <CheckCircle sx={{ color: 'var(--hp-success)', fontSize: 16 }} />
            Grounded AI chat with meeting transcript citations
          </Typography>
          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: 'var(--hp-text)', fontWeight: 500 }}>
            <CheckCircle sx={{ color: 'var(--hp-success)', fontSize: 16 }} />
            Automated Jira epics, user stories & action item generation
          </Typography>
          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: 'var(--hp-text)', fontWeight: 500 }}>
            <CheckCircle sx={{ color: 'var(--hp-success)', fontSize: 16 }} />
            Real-time Scrum health scores & delivery blocker analysis
          </Typography>
        </Box>

        {/* Primary Google Auth Button */}
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleGoogleClick}
          disabled={loading}
          startIcon={
            loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                />
              </svg>
            )
          }
          sx={{
            bgcolor: '#FFFFFF',
            color: '#1F2937',
            border: '1px solid #E5E7EB',
            borderRadius: '14px',
            fontWeight: 700,
            fontSize: '0.96rem',
            textTransform: 'none',
            py: 1.4,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            '&:hover': {
              bgcolor: '#F9FAFB',
              boxShadow: '0 6px 16px rgba(0,0,0,0.12)'
            }
          }}
        >
          {loading ? 'Authenticating with Google...' : 'Continue with Google'}
        </Button>

        {/* Demo Mode Fallback & Privacy Link */}
        <Box sx={{ textAlign: 'center', pt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.8, alignItems: 'center' }}>
          <Button
            variant="text"
            size="small"
            onClick={handleDemoClick}
            sx={{
              color: 'var(--hp-text-secondary)',
              textTransform: 'none',
              fontSize: '0.82rem',
              '&:hover': { color: 'var(--hp-primary)', bgcolor: 'transparent' }
            }}
          >
            Or explore with instant Demo Account →
          </Button>

          <Button
            variant="text"
            size="small"
            onClick={() => {
              closeSignInModal();
              navigate('/privacy');
            }}
            sx={{
              color: 'var(--hp-primary)',
              textTransform: 'none',
              fontSize: '0.78rem',
              p: 0,
              '&:hover': { textDecoration: 'underline', bgcolor: 'transparent' }
            }}
          >
            Read our Privacy Policy
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SignInDialog;
